import { useCallback } from 'react';
import { useAppController, useMessageHandler } from '../controllers/AppController';
import { useTranslation } from 'react-i18next';
import { toast, Id, ToastOptions, UpdateOptions } from 'react-toastify';
import throttle from 'lodash/throttle';

import useConfigurationStore from '../state/ConfigurationStore';
import usePrompterSession from '../state/PrompterSessionState';
import { EndpointRole, SetScrollSpeedMessage } from '@fluidprompter/core';

let globalToastId: Id = 0;

function useConfigMessageHandlers() {

  const appController = useAppController();

  const { t } = useTranslation('prompter');

  const showNotification = useCallback(throttle(() => {
    const toastOptions: UpdateOptions = {
      type: 'error',
      isLoading: false,
      autoClose: 5000,
      hideProgressBar: false,
      onClose: () => {
        globalToastId = 0;
      }
    };

    if(globalToastId) {
      toast.update(globalToastId, toastOptions);
      return;
    }

    globalToastId = toast(t('speedmenu.scrollspeedislocked'), toastOptions as ToastOptions);

  }, 6000), [t]);

  const doSetUserScrollSpeed = useCallback(async function (msg?: SetScrollSpeedMessage) {
    const state = useConfigurationStore.getState();
    if(state.scrollSpeedLocked) {
      showNotification();
      return;
    }

    if(msg && state.scrollSpeed !== msg.speed) {
      state.setUserScrollSpeed(msg.speed, msg.reverse);
    }
  }, [showNotification]);

  useMessageHandler('setscrollspeed', (e) => {
    const { message, originatedRemotely } = e;
    const { sender, speed: proposedSpeed, reverse: proposedReverse } = message;
    const senderIsRemote = sender?.role === EndpointRole.Remote;

    // performance.mark('setscrollspeed-start');

    const prompterSession = usePrompterSession.getState();

    //
    // If we receive Play/Pause/Edit/Navigate commands from a remote peer, that peer is currently
    // acting as prompter leader.
    //
    // If this message was sent by a prompter (and not a headless remote), then we will re-evaluate
    // whether we are the current leader or not.
    const isLeader = e.checkIAmLeader(prompterSession);

    //
    // If this SetScrollSpeedMessage originated from the local prompter, let's send it to all
    // connected peers.
    //
    e.sendToPeers = !originatedRemotely;

    //
    // Update our SenderInfo as we do not serialize/deserialize the properties of
    // SetScrollSpeedMessage, instead we rely on using SenderInfo as the universal way of
    // synchronizing scroll speed between peers.
    //
    if(!originatedRemotely && sender) {
      sender.scrollSpeed = proposedSpeed;
      sender.scrollReversed = proposedReverse;
    }

    //
    // If this SetScrollSpeedMessage originated remotely from a cloud remote, and we are the
    // current leader, then we want to dispatch a new SetScrollMessage to peers which will contain
    // the necessary `SenderInfo` meta data for prompter sync'ing.
    //
    if(originatedRemotely && senderIsRemote && isLeader) {
      e.dispatchMessage(new SetScrollSpeedMessage(proposedSpeed, proposedReverse));

      // We will receive our own dispatched `SetScrollSpeedMessage` above, so no need to continue
      // processing the message from the cloud remote.
      return;
    }

    //
    // If the sender is a remote and I'm not the leader, ignore this message.
    // The Leader who receives this message will dispatch a new SetScrollSpeedMessage.
    //
    if(originatedRemotely && senderIsRemote && !isLeader) {
      // The current leader will process the remote command within the context of the current
      // scroll position and transmit a more accurate message to all peers.
      return;
    }

    if(originatedRemotely && !isLeader && appController.endpointRole !== EndpointRole.Remote) {
      //
      // Scale scroll speed from the leader based on SenderInfo metadata.
      e.syncScrollSpeed();

      // performance.mark('setscrollspeed-syncScrollSpeed()');
    } else {
      const state = useConfigurationStore.getState();
      if(state.scrollSpeedLocked) {
        e.sendToPeers = false;
      }

      //
      // Does not scale scroll speed.
      doSetUserScrollSpeed(message);
      // performance.mark('setscrollspeed-doSetUserScrollSpeed()');
    }
    // const measureInfo = performance.measure('setscrollspeed-end', 'setscrollspeed-start');
    // console.log(`setscrollspeed duration = ${measureInfo.duration}`);
  });

  return {
    setScrollSpeed: doSetUserScrollSpeed,
  };
}

export default useConfigMessageHandlers;